import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import { FormattedMessage, useIntl } from 'react-intl';
import { useParams, useSearchParams } from 'react-router-dom';
import { Button } from '@components/common/Button';
import { FormControl } from '@components/common/FormControl/FormControl';
import { ApplicationFormPageEnum } from '../types';
import { employmentQuestions } from './questions';
import { EmploymentStatusEnum, HowEmployedEnum, IEmploymentForm } from './types';
import { useNavigateBack } from '@hooks/useNavigateBack';
import { parseApplicationInProgress } from '../helpers';
import { useApplicationInProgressById } from '@queries/applications/useApplicationInProgressById';
import { getCurrentRoute } from '@utils/location';
import { applicationFormApi } from '@api/applicationForm/applicationFormApi';
export const Employment = () => {
  const intl = useIntl();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigateBack = useNavigateBack();
  const { id: applicationId = '' } = useParams();
  const currentRoute = getCurrentRoute();
  const { mutate: patchApplicationInProgress } = useMutation(applicationFormApi.patchApplicationInProgress);
  const { data: applicationInProgress } = useApplicationInProgressById(applicationId);
  const formattedResponse =
    applicationInProgress?.[currentRoute] && parseApplicationInProgress(applicationInProgress[currentRoute]);
  const requiredFieldErrorMessage = intl.formatMessage({ id: 'common.error.requiredField' });

  const form = useForm<IEmploymentForm>({
    defaultValues: {
      employmentStatus: '',
      howEmployed: '',
      zeroHoursContract: '',
      typeOfSelfEmployment: '',
    },
  });
  const formFields = form.watch();

  useEffect(() => {
    const isEmploymentStatusFieldIsDirty = Object.keys(form.formState.dirtyFields).includes('employmentStatus');
    const isHowEmployedFieldIsDirty = Object.keys(form.formState.dirtyFields).includes('howEmployed');

    if (isEmploymentStatusFieldIsDirty) {
      form.reset({ ...formFields, howEmployed: '', zeroHoursContract: '', typeOfSelfEmployment: '' });
    } else if (isHowEmployedFieldIsDirty) {
      form.reset({ ...formFields, zeroHoursContract: '', typeOfSelfEmployment: '' });
    }
  }, [formFields.employmentStatus, formFields.howEmployed]);

  useEffect(() => {
    if (formattedResponse) form.reset({ ...formFields, ...formattedResponse });
  }, [applicationInProgress]);

  const isEmployed = formFields.employmentStatus === EmploymentStatusEnum.EMPLOYEE;
  const isEmployedPermanent = formFields.howEmployed === HowEmployedEnum.PERMANENT;
  const isSelfEmployed = formFields.employmentStatus === EmploymentStatusEnum.SELF_EMPLOYED;
  const isUnemployed = formFields.employmentStatus === EmploymentStatusEnum.UNEMPLOYED;
  const isRetired = formFields.employmentStatus === EmploymentStatusEnum.RETIRED;
  const isAnyFieldWithError = Boolean(Object.values(form.formState.errors).length);

  const submitFormHandler = async (formData: IEmploymentForm) => {
    const deleteFollowingPages = formattedResponse && JSON.stringify(formattedResponse) !== JSON.stringify(formData);
    const formValues = Object.values(formData).filter(value => value);
    const filteredFormData = Object.entries(formData).filter(([, value]) => value);

    const questions = filteredFormData.map(([key, answer]) => ({
      question: intl.formatMessage({ id: employmentQuestions[key].labelKey }),
      key,
      answer,
    }));

    await patchApplicationInProgress({ questions, currentRoute, applicationId, deleteFollowingPages });

    const section =
      isUnemployed || isRetired ? ApplicationFormPageEnum.OTHER_INCOME : ApplicationFormPageEnum.INDUSTRY_AND_ROLE;

    searchParams.set('section', section);
    searchParams.set('employment', formValues.join('_'));

    setSearchParams(searchParams);
  };

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(submitFormHandler)}>
        <div className='mt-5'>
          <FormattedMessage id={employmentQuestions.employmentStatus.labelKey} tagName={'h4'} />
          <FormControl
            rules={{ required: requiredFieldErrorMessage }}
            name={'employmentStatus'}
            radioOptions={employmentQuestions.employmentStatus.options}
            radioGroupProps={{ className: 'mt-2' }}
            isWide
          />
        </div>

        {isEmployed && (
          <div className='mt-5'>
            <FormattedMessage id={employmentQuestions.howEmployed.labelKey} tagName={'h4'} />
            <FormattedMessage id={employmentQuestions.howEmployed.note} tagName={'p'} />
            <FormControl
              name={'howEmployed'}
              rules={{ required: isEmployed && requiredFieldErrorMessage }}
              radioOptions={employmentQuestions.howEmployed.options}
              radioGroupProps={{ className: 'mt-2' }}
            />
          </div>
        )}

        {isEmployed && isEmployedPermanent && (
          <div className='mt-5'>
            <FormattedMessage id={employmentQuestions.zeroHoursContract.labelKey} tagName={'h4'} />
            <FormControl
              rules={{ required: isEmployed && isEmployedPermanent && requiredFieldErrorMessage }}
              name={'zeroHoursContract'}
              radioOptions={employmentQuestions.zeroHoursContract.options}
              radioGroupProps={{ className: 'mt-2' }}
            />
          </div>
        )}

        {isSelfEmployed && (
          <div className='mt-5'>
            <FormattedMessage id={employmentQuestions.typeOfSelfEmployment.labelKey} tagName={'h4'} />
            <FormControl
              rules={{ required: isSelfEmployed && requiredFieldErrorMessage }}
              name={'typeOfSelfEmployment'}
              radioOptions={employmentQuestions.typeOfSelfEmployment.options}
              radioGroupProps={{ className: 'mt-2' }}
              isWide
            />
          </div>
        )}

        <div className={'mt-8 flex justify-between'}>
          <Button variant={'outlined'} onClick={navigateBack}>
            <FormattedMessage id={'cta.back'} />
          </Button>

          <Button variant={'contained'} type={'submit'} disabled={isAnyFieldWithError}>
            <FormattedMessage id={'cta.continue'} />
          </Button>
        </div>
      </form>
    </FormProvider>
  );
};
